import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { mask } from '../../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { ink: "var(--pr-ink)", gold: "var(--pr-gold)", cream: "var(--pr-bg)" };

const fmtMoney = (v) => (v == null ? ", " : mask(`$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`));

const ConfirmBlock = ({ confirmed, setConfirmed, onSubmit, canSubmit, submitting }) => (
  <>
    <label className="flex items-start gap-3 mb-5 cursor-pointer" data-testid="season-setup-confirm-label">
      <input
        type="checkbox"
        checked={confirmed}
        onChange={(e) => setConfirmed(e.target.checked)}
        className="mt-0.5 w-4 h-4 flex-shrink-0 accent-[#F5CB5C]"
        data-testid="season-setup-confirm-checkbox"
      />
      <span className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
        I understand that these numbers set my first-quarter rhythm. I can recalibrate at Quarterly Tax Days, but I won't change the race mid-quarter. My payday delay is locked for the full 52-week race.
      </span>
    </label>
    <button
      onClick={onSubmit}
      disabled={!canSubmit || submitting}
      className="w-full py-4 rounded-full font-bold text-base transition-transform hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100"
      style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
      data-testid="season-setup-start-btn"
    >
      {submitting ? "Setting up your race…" : "Start My Payday Relay Race"}
    </button>
  </>
);

export const SeasonSetupGate = ({ seasonSetup, recalibrated = false, onClose, onDone }) => {
  const navigate = useNavigate();
  const stage = seasonSetup.stage;
  const required = stage === "required" || stage === "build_pending";
  const buildPending = stage === "build_pending";
  const cur = seasonSetup.current || {};
  const recal = seasonSetup.recalibrated || {};
  const [view, setView] = useState(
    buildPending ? "build" : recalibrated && recal.weekly_target ? "review" : "options"
  );
  const [keepSelected, setKeepSelected] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (choice) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/caddy/season-setup/lock`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice, confirmed: true }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const detail = data?.detail;
        toast.error(typeof detail === "string" ? detail : "Couldn't lock your season setup, try again");
        return;
      }
      setResult(data);
      if (data.sheet_built) {
        confetti({ particleCount: 180, spread: 100, origin: { y: 0.4 }, colors: [C.gold, "#3E8E4E", "#FFFFFF"] });
      }
    } catch {
      toast.error("Couldn't reach the pit wall, check your connection and try again");
    } finally {
      setSubmitting(false);
    }
  };

  const goRecalibrate = () => navigate("/app/calculator?from=season-setup");

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto"
      style={{ background: "rgba(20,14,50,0.96)", backdropFilter: "blur(6px)" }}
      data-testid="season-setup-gate"
    >
      <div className="min-h-full flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          {result ? (
            <div className="rounded-3xl p-7 text-center" style={{ background: "var(--pr-ink-surface)", border: "1px solid rgba(245,203,92,0.35)" }} data-testid="season-setup-success">
              {result.sheet_built ? (
                <>
                  <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }}>
                    Your Payday Relay race is on.
                  </h2>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Your fresh 52-week sheet is live in your Google Drive. Your Sprint sheet stays right next to it as your warm-up record.
                  </p>
                  {result.sheet_url && (
                    <a href={result.sheet_url} target="_blank" rel="noreferrer"
                       className="inline-block px-6 py-3 rounded-full font-bold text-sm mb-3"
                       style={{ background: "rgba(255,255,255,0.12)", color: "#FFFFFF" }}
                       data-testid="season-setup-open-sheet">
                      Open my 52-week sheet →
                    </a>
                  )}
                </>
              ) : result.starts_at_conversion ? (
                <>
                  <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }}>
                    Locked in.
                  </h2>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Your first quarter is set. The moment your Sprint converts, your fresh 52-week sheet gets built and your Payday Relay race begins.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }}>
                    Season locked.
                  </h2>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Your 52-week race is set up in the app. Connect Google any time to build your season sheet in your own Drive.
                  </p>
                  {result.setup_url && (
                    <a href={result.setup_url}
                       className="inline-block px-6 py-3 rounded-full font-bold text-sm mb-3"
                       style={{ background: "rgba(255,255,255,0.12)", color: "#FFFFFF" }}
                       data-testid="season-setup-connect-google">
                      Connect Google &amp; build my sheet →
                    </a>
                  )}
                </>
              )}
              <div>
                <button onClick={onDone}
                        className="inline-block px-8 py-3.5 rounded-full font-bold text-base transition-transform hover:scale-105"
                        style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
                        data-testid="season-setup-done-btn">
                  Go to my dashboard →
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl p-6 sm:p-8" style={{ background: "var(--pr-ink-surface)", border: "1px solid rgba(245,203,92,0.35)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] mb-2" style={{ color: C.gold }}>
                Season Setup
              </p>
              <h2 className="text-2xl sm:text-3xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }}>
                Start Your Payday Relay Race
              </h2>

              {view === "build" && (
                <>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.85)" }} data-testid="season-setup-build-pending-note">
                    Your first-quarter settings are locked. We just need to finish building your fresh 52-week sheet. Tap below to complete your setup.
                  </p>
                  <button
                    onClick={() => submit(seasonSetup.choice || "keep")}
                    disabled={submitting}
                    className="w-full py-4 rounded-full font-bold text-base transition-transform hover:scale-[1.01] disabled:opacity-40"
                    style={{ background: "var(--pr-gold)", color: "var(--pr-on-gold)" }}
                    data-testid="season-setup-start-btn"
                  >
                    {submitting ? "Setting up your race…" : "Start My Payday Relay Race"}
                  </button>
                </>
              )}

              {view === "review" && (
                <>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Fresh from your Relay Calculator, here's the plan for your first quarter.
                  </p>
                  <div className="rounded-2xl p-5 mb-5 border-2" style={{ borderColor: C.gold, background: "rgba(245,203,92,0.12)" }} data-testid="season-setup-recal-card">
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: C.gold }}>Your recalibrated plan</p>
                    <p className="text-2xl font-bold" style={{ color: "#FFFFFF" }} data-testid="season-setup-recal-target">
                      {fmtMoney(recal.weekly_target)}<span className="text-sm font-normal" style={{ color: "rgba(255,255,255,0.7)" }}>/week</span>
                    </p>
                    <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>
                      {recal.days_per_week ? `${recal.days_per_week} days a week` : ""}
                      {recal.savings_goal ? ` · ${fmtMoney(recal.savings_goal)} savings goal` : ""}
                      {recal.debt_amount ? ` · ${fmtMoney(recal.debt_amount)} debt payoff` : ""}
                    </p>
                    {recal.goal_statement && (
                      <p className="text-xs italic mt-2" style={{ color: "rgba(255,255,255,0.6)" }}>"{recal.goal_statement}"</p>
                    )}
                    {cur.payday_delay != null && (
                      <p className="text-[11px] mt-3" style={{ color: "rgba(255,255,255,0.55)" }}>
                        Your {cur.payday_delay}-day payday delay carries into the race and stays locked.
                      </p>
                    )}
                  </div>
                  <ConfirmBlock confirmed={confirmed} setConfirmed={setConfirmed}
                                onSubmit={() => submit("recalibrate")} canSubmit={confirmed} submitting={submitting} />
                  <div className="flex justify-center gap-5 mt-3">
                    <button onClick={() => { setView("options"); setConfirmed(false); }}
                            className="text-xs underline" style={{ color: "rgba(255,255,255,0.55)" }}
                            data-testid="season-setup-back-to-options">
                      ← See my options instead
                    </button>
                    <button onClick={goRecalibrate}
                            className="text-xs underline" style={{ color: "rgba(255,255,255,0.55)" }}
                            data-testid="season-setup-reopen-calc">
                      Re-open my Relay Calculator
                    </button>
                  </div>
                </>
              )}

              {view === "options" && (
                <>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: "rgba(255,255,255,0.85)" }}>
                    Your 4-Relay Sprint helped you test the rhythm. Before your full 52-week race begins, choose whether to keep your Sprint settings or recalibrate your relay.
                  </p>
                  <div className="space-y-3 mb-5">
                    <button
                      type="button"
                      onClick={() => setKeepSelected(true)}
                      data-testid="season-setup-keep"
                      className="w-full text-left rounded-2xl p-4 border-2 transition-colors"
                      style={{
                        borderColor: keepSelected ? C.gold : "rgba(255,255,255,0.2)",
                        background: keepSelected ? "rgba(245,203,92,0.12)" : "rgba(255,255,255,0.05)",
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center"
                              style={{ borderColor: keepSelected ? C.gold : "rgba(255,255,255,0.4)" }}>
                          {keepSelected && <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--pr-gold)" }} />}
                        </span>
                        <span className="font-bold text-base" style={{ color: "#FFFFFF" }}>Keep My Sprint Settings</span>
                      </div>
                      <p className="text-xs mt-2 ml-8" style={{ color: "rgba(255,255,255,0.7)" }}>
                        {fmtMoney(cur.weekly_target)}/week
                        {cur.days_per_week ? ` · ${cur.days_per_week} days a week` : ""}
                        {cur.payday_delay != null ? ` · ${cur.payday_delay}-day payday delay` : ""}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={goRecalibrate}
                      data-testid="season-setup-recalibrate"
                      className="w-full text-left rounded-2xl p-4 border-2 transition-colors hover:border-[#F5CB5C]"
                      style={{ borderColor: "rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)" }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="w-5 h-5 rounded-full border-2 flex-shrink-0" style={{ borderColor: "rgba(255,255,255,0.4)" }} />
                          <span className="font-bold text-base" style={{ color: "#FFFFFF" }}>Recalibrate My Relay</span>
                        </div>
                        <span className="text-lg" style={{ color: C.gold }}>→</span>
                      </div>
                      <p className="text-xs mt-2 ml-8" style={{ color: "rgba(255,255,255,0.7)" }}>
                        Re-run your Relay Calculator with everything your Sprint taught you, savings, debt payoff, PTO, taxes, and rebuild your first quarter.
                      </p>
                    </button>
                  </div>
                  <ConfirmBlock confirmed={confirmed} setConfirmed={setConfirmed}
                                onSubmit={() => submit("keep")} canSubmit={keepSelected && confirmed} submitting={submitting} />
                </>
              )}

              {!required && (
                <button onClick={onClose}
                        className="w-full mt-3 py-2 text-xs underline"
                        style={{ color: "rgba(255,255,255,0.55)" }}
                        data-testid="season-setup-later-btn">
                  I'll decide later, back to my Sprint
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
